import React from 'react';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import BucketTestProductInstallment from '@components/bucketTest/bucket-test-product-installment';
import TagContainer from '@enums/tagContainer';
import { useAnalytics } from '@context/analytics/analytics.context';
import { NegotiationProvider } from '@context/negotiation.context';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useProductQuery from '@hooks/product/use-product-query';
import UserBadge from '@enums/userBadge';
import WelcomeVoucherWrapper from '@components/catalog/welcome-voucher/welcome-voucher-wrapper';
import dynamic from 'next/dynamic';
import SellerOnVacationWrapper from '@components/molecules/products/product-details/components/seller-on-vacation/seller-on-vacation-wrapper';
import useWindowSize from '@hooks/use-window-size';
import useFeatureFlag from '@hooks/use-feature-flags';
import clsx from 'clsx';
import ProductSellerBadge from '../product-seller-badge/product-seller-badge';
import AdditionalDetails from './components/additional-details/additional-details';
import CTAProduct from './components/CTA-product/CTA-product';
import ProductCharacteristics from './components/product-characteristics/product-characteristics';
import ProductPrice from './components/product-price/product-price';
import styles from './product-details.module.scss';

const ProductSellerActions = dynamic(() => import('./components/product-seller-actions/product-seller-actions'));

const ProductAuthInfo = dynamic(() => import('./components/product-authentication-infos/product-authentication-infos'));

const ShippingRestriction = dynamic(() => import('./components/shipping-restriction/shipping-restriction'));

type ProductDetailsProps = {
  isWelcomeOfferLDFlagEnabled?: boolean;
};

const ProductDetails: React.FC<ProductDetailsProps> = ({ isWelcomeOfferLDFlagEnabled }) => {
  const { t } = useTranslation();
  const { sendEvent } = useAnalytics();
  const { productShipping } = useProductShippingQuery();
  const { product, userTransactionRole, isProductDirectShippableForCurrentUser } = useProductQuery();
  const useMonetization = product.pricingBreakdown;
  const hasMandatoryAuthentication = useMonetization && (product.inStock || !isProductDirectShippableForCurrentUser);

  const isSeller: boolean = userTransactionRole === 'seller';
  const isOfficialStore: boolean = product.seller.badges?.includes(UserBadge.OfficialStore);
  const IsConsignment: boolean =
    product.consignment && !(product.consignment && product.seller.internalAccount) && !isOfficialStore;

  const shouldShowShippingRestriction = productShipping?.prohibited && !isSeller;
  const { isWiderThanMd } = useWindowSize();
  const { isSemPDP } = useFeatureFlag();

  const trackConsignmentClick = (e): void => {
    sendEvent(
      {
        type: 'event',
        container: TagContainer.Analytics,
        payload: {
          event: 'concierge_service',
          position: 'user_details',
          env_template: 'product',
        },
      },
      {
        type: 'ClickConsignmentCTA - productPage',
        container: TagContainer.Media,
        payload: {
          eventCategory: 'product',
          eventAction: 'concierge_service',
          eventLabel: 'profil : product_product_details',
        } as never,
      },
    );
    e.stopPropagation();
  };

  // seller, price, ds, condition, klarna, discount, cta, actions
  return (
    <div className={clsx(styles.productDetails, { [styles['productDetails--semPdpIteration']]: isSemPDP })}>
      <div className={styles.productDetails__top}>
        <div className={styles.productDetails__resume}>
          {product.seller && (
            <div className={styles.productDetails__seller}>
              <div className={styles.productDetails__seller__badge}>
                <ProductSellerBadge seller={product.seller} />
              </div>
              <div className={styles.productDetails__seller__text}>
                <a
                  className={styles.productDetails__seller__text__link}
                  href={`/profile/${product?.seller?.id}`}
                  rel="noopener"
                >
                  {`${t('PRODUCT.DETAILS.SELLING_BY')} ${product?.seller.firstname}`}
                </a>

                {IsConsignment && (
                  <a
                    // TODO: Update this anchor to <Link> when "CONSIGNMENT.SERVICE.LINK" is updated to /journal/...
                    className={styles.productDetails__sellerText__link}
                    href={t('CONSIGNMENT.SERVICE.LINK')}
                    onClick={(e) => trackConsignmentClick(e)}
                    rel="noopener"
                  >
                    <u className={styles.productDetails__sellerText__consignment}>{t('CONSIGNMENT.SERVICE')}</u>
                  </a>
                )}
              </div>
            </div>
          )}
          {/* Seller actions */}
          {isSeller && <ProductSellerActions product={product} />}
          <ul className={styles.productDetails__resume__list}>
            {product?.inStock && (
              <li
                className={styles.productDetails__resume__express}
                data-cy="express-delivery-option"
              >
                <Icon
                  name="express"
                  className={styles.productDetails__resume__express__icon}
                  ariaHidden
                />
                <span>{t('DELIVERY_OPTS.EXPRESS')}</span>
              </li>
            )}
            {/* Product price */}
            <li className={styles.productDetails__resume__productPrice}>
              <ProductPrice
                isSeller={isSeller}
                product={product}
                directShippingEligible={isProductDirectShippableForCurrentUser}
                hasMandatoryAuthentication={hasMandatoryAuthentication}
              />
            </li>
            {/* Product characteristics */}
            <li className={styles.productDetails__resume__characteristics}>
              <ProductCharacteristics product={product} />
            </li>
            {/* Product auth */}
            {!isProductDirectShippableForCurrentUser && (
              <li className={styles.productDetails__resume__auth}>
                <ProductAuthInfo hasMandatoryAuthentication={hasMandatoryAuthentication} />
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Pay-in-installment provider */}
      <div className={styles.productDetails__resume__installment}>
        <BucketTestProductInstallment product={product} />
      </div>

      {!isWelcomeOfferLDFlagEnabled && <WelcomeVoucherWrapper customClass={styles.productDetails__welcomeVoucher} />}
      {shouldShowShippingRestriction && <ShippingRestriction />}
      <SellerOnVacationWrapper />
      <NegotiationProvider>{productShipping && <CTAProduct />}</NegotiationProvider>

      {!(isSemPDP && !isWiderThanMd) && <AdditionalDetails />}
    </div>
  );
};

export default ProductDetails;
